import { useCallback, useEffect, useRef, useState } from "react";
import type { ApiJob, JobStatusName } from "@tradingdroid/frontends-types";

import { tdApi } from "@src/services/api/tdApi";
import { getApiErrorMessage } from "@src/utils/apiError";
import { isStopAcknowledged } from "@src/utils/jobPauseResume";

export const LIVE_JOB_REFRESH_MS = 3000;

const IN_FLIGHT_STATUSES: readonly JobStatusName[] = ["queued", "claimed", "running"];

/**
 * Whether the job can still change on its own. A paused or cancelled optimization counts until its
 * worker acknowledges the stop, because that is when its result is rewritten.
 */
export const jobNeedsRefresh = (job: ApiJob): boolean =>
  IN_FLIGHT_STATUSES.includes(job.status_name) ||
  (job.job_type_name === "optimization" &&
    (job.status_name === "paused" || job.status_name === "cancelled") &&
    !isStopAcknowledged(job));

/** One job, loaded on mount and re-read every few seconds until it stops changing. */
export const useLiveJob = (jobId: number) => {
  const [job, setJob] = useState<ApiJob | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Bumped whenever an action's result is applied, so a read started before it is dropped on arrival.
  const generationRef = useRef(0);

  const applyJob = useCallback((next: ApiJob) => {
    generationRef.current += 1;
    setJob(next);
  }, []);

  useEffect(() => {
    if (Number.isNaN(jobId)) return;

    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await tdApi.getJob(jobId);
        if (cancelled) return;
        if (!result) {
          setError(`Job ${jobId} not found`);
          return;
        }
        setJob(result);
      } catch (err) {
        if (cancelled) return;
        setError(getApiErrorMessage(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  const refreshing = job !== null && job.id === jobId && jobNeedsRefresh(job);

  useEffect(() => {
    if (!refreshing) return;

    let cancelled = false;
    let inFlight = false;
    const refresh = async () => {
      // A slow read must not overlap the next tick and land out of order.
      if (inFlight) return;
      inFlight = true;
      const generation = generationRef.current;
      try {
        const fresh = await tdApi.getJob(jobId);
        if (!cancelled && fresh && generation === generationRef.current) setJob(fresh);
      } catch {
        // A failed refresh keeps the last row on screen; the next tick tries again.
      } finally {
        inFlight = false;
      }
    };

    const timer = setInterval(refresh, LIVE_JOB_REFRESH_MS);
    return () => {
      cancelled = true;
      generationRef.current += 1;
      clearInterval(timer);
    };
  }, [refreshing, jobId]);

  return { job, applyJob, loading, error };
};
